/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { LISTING_STATUS } from './constants/listingStatus';
import {
  findListingEntity,
  getListingProductIdFromState,
  listListingVariantsForChannel,
} from './helpers/entityReadHelpers';
import { upsertListing, upsertListingVariant } from './helpers/entityAdapter';
import { setActiveTabByProductChannel } from './helpers/uiAdapter';

export const removeProductLiveSnapshot = createAsyncThunk(
  'products/removeProductLiveSnapshot',
  async ({ productId, channelId }, { getState, extra: { Logger, Toast, sdk } }) => {
    try {
      const listingProductId = getListingProductIdFromState(getState().products, productId, channelId);

      if (!listingProductId) {
        throw new Error(`Listing not found for channel ${channelId}`);
      }

      await sdk.products.deleteLiveListing(listingProductId);

      return { productId, channelId };
    } catch (error) {
      Toast(
        'Listing is not removed. Refresh and try again',
        { autoClose: false, type: 'error' },
      );
      Logger.error(new Error('Error removing live from listing'), { error }, [
        'RemoveLiveFromListingFailed',
        { errorMessage: 'Live data is not removed on the backend.' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(removeProductLiveSnapshot.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(removeProductLiveSnapshot.fulfilled, (state, { payload }) => {
      const { productId, channelId } = payload;
      state.isBusy = false;

      const existingListing = findListingEntity(state, { masterProductId: productId, channelId });
      if (!existingListing?._id) return;

      setActiveTabByProductChannel(state, productId, channelId, LISTING_STATUS.DRAFT);

      upsertListing(state, {
        ...existingListing,
        masterProductId: productId,
        live: null,
        listingStatus: LISTING_STATUS.DRAFT,
      });

      listListingVariantsForChannel(state, productId, channelId).forEach((channelEntry) => {
        if (!channelEntry?._id) return;

        upsertListingVariant(state, {
          ...(state.entities?.listingVariants?.[channelEntry._id] || channelEntry),
          _id: channelEntry._id,
          live: null,
        });
      });
    })
    .addCase(removeProductLiveSnapshot.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot remove live from listing';
    });
};
